import { Link } from 'react-router-dom';
import { Heart, Mail, Github, Twitter, Instagram, Linkedin, Youtube, Facebook } from 'lucide-react';
import { LogoMark } from '@/components/ui/LogoMark';
import { useLocation } from 'react-router-dom';
import { useSite } from '@/lib/useSite';
import { LanguageSwitcher } from '@/components/i18n/LanguageSwitcher';
import { languageAlternates } from '@/lib/languageTarget';

const footerLinks = {
  product: [
    { key: 'blog' as const, href: '/blog' },
    { key: 'astrology' as const, href: '/astrology' },
    { key: 'pricing' as const, href: '/pricing' },
    { key: 'myJourney' as const, href: '/dashboard' },
  ],
  features: [
    { key: 'aiAstrologer' as const, href: '/ai-astrologer' },
    { key: 'freeKundli' as const, href: '/free-kundli' },
    { key: 'kundliMatching' as const, href: '/kundli-matching' },
    { key: 'mangalDosha' as const, href: '/mangal-dosha-calculator' },
    { key: 'nakshatraCalc' as const, href: '/nakshatra-calculator' },
    { key: 'dashaCalc' as const, href: '/dasha-calculator' },
    { key: 'lagnaCalc' as const, href: '/lagna-calculator' },
    { key: 'panchang' as const, href: '/panchang' },
    { key: 'rashifal' as const, href: '/rashifal' },
    { key: 'nakshatras' as const, href: '/nakshatra' },
    { key: 'allTools' as const, href: '/tools' },
  ],
  company: [
    { key: 'about' as const, href: '/about' },
    { key: 'contact' as const, href: '/contact' },
    { key: 'privacy' as const, href: '/privacy' },
    { key: 'terms' as const, href: '/terms' },
  ],
};

export function Footer() {
  const currentYear = new Date().getFullYear();
  const { locale, s, path } = useSite();
  const f = s.footer;
  const alternates = languageAlternates(useLocation().pathname);

  return (
    <footer className="relative z-10 border-t border-white/10 bg-[#050310]/80 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to={path('/')} className="flex items-center gap-2 group mb-4">
              <LogoMark size={32} className="transition-transform group-hover:scale-105" />
              <span className="font-display font-bold text-lg text-white">
                Kundali <span className="text-gold-400">AI</span>
              </span>
            </Link>
            <p className="text-sm text-gray-400 mb-4 max-w-xs">{f.tagline}</p>
            <div className="flex items-center gap-4">
              <a
                href="mailto:support@kundaliai.com"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com/kundaliai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://github.com/kundaliai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="GitHub"
              >
                <Github className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com/kundaliai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com/company/kundaliai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://youtube.com/@kundali-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com/kundaliai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="font-display text-sm font-semibold text-white mb-4">{f.product}</h3>
            <ul className="space-y-3">
              {footerLinks.product.filter((link) => locale === 'en' || link.key !== 'blog').map((link) => (
                <li key={link.key}>
                  <Link
                    to={path(link.href)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {f.links[link.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Features Links */}
          <div>
            <h3 className="font-display text-sm font-semibold text-white mb-4">{f.features}</h3>
            <ul className="space-y-3">
              {footerLinks.features.filter((link) => locale === 'en' || link.key !== 'nakshatras').map((link) => (
                <li key={link.key}>
                  <Link
                    to={path(link.href)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {f.links[link.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="font-display text-sm font-semibold text-white mb-4">{f.company}</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.key}>
                  <Link
                    to={path(link.href)}
                    className="text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {f.links[link.key]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">{f.rights(currentYear)}</p>
          {alternates && (
            <LanguageSwitcher current={locale} locales={alternates.locales} pathFor={alternates.pathFor} label={s.nav.language} />
          )}
          <p className="text-sm text-gray-500 flex items-center gap-1">
            {f.madeWith[0]} <Heart className="w-4 h-4 text-red-500" /> {f.madeWith[1]}
          </p>
        </div>
      </div>
    </footer>
  );
}
